//grimdark/client/src/screens/Landing.js
import { useEffect, useState } from 'react';
import { TextureLoader } from 'three';
import { Canvas } from '@react-three/fiber';

//screens
import AccessScreen from '../AccessScreen';

//components
import GearChains from '../../components/UI/GearChains/GearChains';
import GrimdarkHud from '../../components/UI/Huds/GrimdarkHud';
import LoadingScreen from '../../components/UI/LoadingScreen/LoadingScreen';
import SpaceSwirlBackgroundWrapper from '../../components/common/Backgrounds/SpaceSwirlBackgroundWrapper';
import Skull from '../../components/common/Skull/Skull';
import FogPlane from '../../components/effects/FogPlane';

//config
import { ACCESS_CODES } from '../../config/ACCESS_CODES';

//images
import {
  hud,
  servo_splash,
  space_image,
  access_code_base,
  access_code_1,
  access_code_2,
  access_code_3,
  access_code_4,
  access_code_5,
  access_code_6,
  access_code_enter,
  access_code_clear,
  port,
} from '../../images';

/**
 * Authoritative timing (JS-owned, deterministic).
 * If you later want CSS to match, just mirror these numbers in _variables.scss.
 */
const TIMING = {
  CHAINS_TOTAL_MS: 3120, // slack + pull + settle (your full raise sequence)
  CHAINS_BUFFER_MS: 120, // small buffer so it always completes cleanly
  MIN_LOADING_MS: 3000,
};

export default function Landing() {
  const [page, setPage] = useState(0);
  const [accessProtocols, setAccessProtocols] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [loadingPhase, setLoadingPhase] = useState('boot');

  const [revealed, setRevealed] = useState(false);
  const [pressedKey, setPressedKey] = useState(null);
  const [skullExiting, setSkullExiting] = useState(false);
  const [hudRaised, setHudRaised] = useState(false);
  useEffect(() => {
    if (!accessProtocols) return;
    setHudRaised(true);
  }, [accessProtocols]);

  useEffect(() => {
    if (!accessProtocols) return;

    // Keep accessProtocols true just long enough for the chains+HUD animation sequence.
    const timer = window.setTimeout(() => {
      setAccessProtocols(false);
    }, TIMING.CHAINS_TOTAL_MS + TIMING.CHAINS_BUFFER_MS);

    return () => window.clearTimeout(timer);
  }, [accessProtocols]);

  const preloadImages = (srcs) =>
    Promise.all(
      srcs.map(
        (src) =>
          new Promise((resolve) => {
            if (!src) return resolve();
            const img = new Image();
            img.decoding = 'async';
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = src;
            if (img.complete) resolve();
          })
      )
    );

  const preloadTexture = (url) =>
    new Promise((resolve) => {
      if (!url) return resolve();
      const loader = new TextureLoader();
      loader.load(
        url,
        () => resolve(),
        undefined,
        () => resolve()
      );
    });

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      const startTime = Date.now();

      const imageSrcs = [
        servo_splash,
        space_image,
        access_code_base,
        access_code_1,
        access_code_2,
        access_code_3,
        access_code_4,
        access_code_5,
        access_code_6,
        access_code_enter,
        access_code_clear,
        hud,
        port,
      ];

      await Promise.all([
        preloadImages(imageSrcs),
        preloadTexture(space_image),
      ]);
      if (!mounted) return;

      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, TIMING.MIN_LOADING_MS - elapsed);

      window.setTimeout(() => {
        if (!mounted) return;
        setLoadingPhase('cooldown');
        // LoadingScreen calls onPhase2Ready when it finishes its own exit/drain
      }, remaining);
    };

    run();

    return () => {
      mounted = false;
    };
  }, []);

  const handleSubmitCode = (code) => {
    if (ACCESS_CODES.includes(code)) {
      setAccessProtocols(true);
      return true;
    }
    return false;
  };

  return (
    <>
      {isLoading && (
        <LoadingScreen
          show={isLoading}
          phase={loadingPhase}
          onPhase2Ready={() => setIsLoading(false)}
        />
      )}

      <div className="onscreen-fade-in-1500ms">
        <img
          src={port}
          alt="port"
          className={`screen-port`}
          draggable={false}
        />

        <div className="landing_screen">
          {!isLoading && page === 0 && (
            <AccessScreen
              setAccessProtocols={setAccessProtocols}
              accessProtocols={accessProtocols}
              revealed={revealed}
              setRevealed={setRevealed}
              pressedKey={pressedKey}
              setPressedKey={setPressedKey}
              handleSubmitCode={handleSubmitCode}
              setSkullExiting={setSkullExiting}
            />
          )}

          {!isLoading && <Skull exiting={skullExiting} />}
          <GrimdarkHud hudRaised={hudRaised} />
        </div>

        <Canvas
          className={`fogCanvas`}
          orthographic
          camera={{ position: [0, 0, 1], zoom: 1 }}
        >
          <FogPlane />
        </Canvas>
        <GearChains move={accessProtocols} />
        <SpaceSwirlBackgroundWrapper />
      </div>
      {/* )} */}
    </>
  );
}
